import React, {
  ReactElement,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  GetNotifications200Response,
  NotificationTypeResponse,
} from "../../codegen-api";
import { AuthContext } from "../AuthContext";
import {
  alertNotificationTypes,
  useNotifications,
} from "../../hooks/api/notifications/useNotifications";

export type NotificationFilter = "all" | "alerts" | "trades" | "account";

const tradeTypes: NotificationTypeResponse[] = [
  NotificationTypeResponse.Fill,
  NotificationTypeResponse.Settled,
  // A fired TP/SL closed (or tried to close) a position — a trade event. The
  // rejected variant is ALSO in alertNotificationTypes, so it shows under both
  // "Trades" and "Alerts"; the filters are not mutually exclusive.
  NotificationTypeResponse.TpslExecuted,
  NotificationTypeResponse.TpslRejected,
];

const accountTypes: NotificationTypeResponse[] = [
  NotificationTypeResponse.DepositFinalized,
  NotificationTypeResponse.WithdrawalFinalized,
  NotificationTypeResponse.Receive,
  NotificationTypeResponse.SendInitiated,
  NotificationTypeResponse.SendFinalized,
  NotificationTypeResponse.EmailVerification,
  NotificationTypeResponse.FarmBoostApplied,
  NotificationTypeResponse.Referral,
  NotificationTypeResponse.Referee,
];

function matchesFilter(
  notification: GetNotifications200Response,
  filter: NotificationFilter
): boolean {
  switch (filter) {
    case "alerts":
      return alertNotificationTypes.includes(notification.notification_type);
    case "trades":
      return tradeTypes.includes(notification.notification_type);
    case "account":
      return accountTypes.includes(notification.notification_type);
    case "all":
    default:
      return true;
  }
}

interface INotificationContextType {
  notifications: GetNotifications200Response[];
  filteredNotifications: GetNotifications200Response[];
  unreadCount: number;
  unreadAlertCount: number;
  showPanel: boolean;
  activeFilter: NotificationFilter;
  isLoading: boolean;

  markAsRead: (id?: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  clearAll: () => Promise<void>;
  dismiss: (id: string) => Promise<void>;
  setShowPanel: (show: boolean) => void;
  setActiveFilter: (filter: NotificationFilter) => void;
}

interface INotificationContextProviderProps {
  children: ReactElement;
}

const noop = () => Promise.resolve();

export const NotificationContext = React.createContext<INotificationContextType>({
  notifications: [],
  filteredNotifications: [],
  unreadCount: 0,
  unreadAlertCount: 0,
  showPanel: false,
  activeFilter: "all",
  isLoading: false,

  markAsRead: noop,
  markAllAsRead: noop,
  clearAll: noop,
  dismiss: noop,
  setShowPanel: () => {},
  setActiveFilter: () => {},
});

export function NotificationContextProvider({
  children,
}: INotificationContextProviderProps) {
  const [showPanel, setShowPanel] = useState<boolean>(false);
  const [activeFilter, setActiveFilter] = useState<NotificationFilter>("all");

  const { apiKey } = useContext(AuthContext);
  const {
    data,
    isValidating,
    mutate,
    markAsRead: markAllAsReadApi,
    markAsReadById,
    dismiss: dismissApi,
    clear: clearApi,
    unreadNotifications,
  } = useNotifications();

  // SWR is configured with revalidateOnMount: false (to avoid pre-login fetches).
  // Trigger an explicit fetch once the user is authenticated so the bell badge
  // shows the correct count without requiring the panel to be opened first.
  useEffect(() => {
    if (apiKey) mutate();
  }, [apiKey, mutate]);

  const notifications = useMemo(() => data ?? [], [data]);

  const filteredNotifications = useMemo(
    () => notifications.filter((n) => matchesFilter(n, activeFilter)),
    [notifications, activeFilter]
  );

  const unreadCount = unreadNotifications.length;

  const unreadAlertCount = useMemo(
    () =>
      unreadNotifications.filter((n) =>
        alertNotificationTypes.includes(n.notification_type)
      ).length,
    [unreadNotifications]
  );

  const markAsRead = useCallback(
    async (id?: string) => {
      if (id) {
        await markAsReadById(id);
      } else {
        await markAllAsReadApi();
      }
    },
    [markAsReadById, markAllAsReadApi]
  );

  const markAllAsRead = useCallback(async () => {
    await markAllAsReadApi();
  }, [markAllAsReadApi]);

  const clearAll = useCallback(async () => {
    await clearApi();
  }, [clearApi]);

  const dismiss = useCallback(
    async (id: string) => {
      await dismissApi(id);
    },
    [dismissApi]
  );

  const value = useMemo(
    () => ({
      notifications,
      filteredNotifications,
      unreadCount,
      unreadAlertCount,
      showPanel,
      activeFilter,
      isLoading: !data && isValidating,
      markAsRead,
      markAllAsRead,
      clearAll,
      dismiss,
      setShowPanel,
      setActiveFilter,
    }),
    [
      notifications,
      filteredNotifications,
      unreadCount,
      unreadAlertCount,
      showPanel,
      activeFilter,
      data,
      isValidating,
      markAsRead,
      markAllAsRead,
      clearAll,
      dismiss,
    ]
  );

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
}
